import SVG from "react-inlinesvg";
import EllipseBig from "buba-shared-logic/assets/icons/ellipseBig.svg";
import EllipseChecked from "buba-shared-logic/assets/icons/ellipseChecked.svg";
import EllipseFailed from "buba-shared-logic/assets/icons/ellipseFailed.svg";
import EllipseHalf from "buba-shared-logic/assets/icons/ellipseHalf.svg";
import EllipseOneThirds from "buba-shared-logic/assets/icons/ellipseOneThirds.svg";
import EllipseTwoThirds from "buba-shared-logic/assets/icons/ellipseTwoThirds.svg";
import cx from "classnames";

import CircleProgress from "../CircleProgress";

import { LEVEL_STATUS } from "./types";
import styles from "./Workflow.scss";
import WorkflowActionButton from "./WorkflowActionButton";

interface NewWorkflowProps {
  items: DashboardAction[];
  id: string;
  confirmAction: (actionId: string, payload: any) => void;
  isCurrentUserAssignedToInquiry: boolean;
  addMessage: (error: string, type: string) => void;
}

type WorkflowAction = {
  buttonLabel: string;
  customModal?: React.ReactNode;
  actionIsRepetable?: boolean;
  onClick?: (actionId: string) => void;
  isDisabled?: boolean;
};

type DashboardAction = {
  id: string;
  type: "dashboard_action";
  actions?: WorkflowAction[];
  assignee?: any;
  attributes: {
    status: LEVEL_STATUS; // Extendable if needed
    steps_completed: number;
    steps_total: number;
    is_required: boolean;
    details: {
      assigned_guarantee_agent: {
        id: number;
        first_name: string;
        last_name: string;
      };
      available_guarantee_agents: {
        id: number;
        first_name: string;
        last_name: string;
      }[];
      creator_feedback_details: string;
    };
    disabled?: boolean;
  };
  heading: string;
  label: string;
  statusLabels?: { [key: string]: { [key: string]: string } };
};

const getLevelIcon = (status: LEVEL_STATUS) => {
  if (status.startsWith(LEVEL_STATUS.FAILED)) {
    return EllipseFailed;
  }

  if (status.startsWith(LEVEL_STATUS.COMPLETED)) {
    return EllipseChecked;
  }

  switch (status) {
    case LEVEL_STATUS.ACTIVE:
      return EllipseBig;
    case LEVEL_STATUS.IN_PROGRESS:
      return EllipseBig;
    case LEVEL_STATUS.EMPTY:
      return EllipseBig;
    case LEVEL_STATUS.FILLED_ONE:
      return EllipseOneThirds;
    case LEVEL_STATUS.FILLED_TWO:
      return EllipseTwoThirds;
    case LEVEL_STATUS.FILLED_HALF:
      return EllipseHalf;
    case LEVEL_STATUS.ERROR:
      return EllipseFailed;
    default:
      return EllipseBig;
  }
};

const calculateProgress = (stepsCompleted: number, stepsTotal: number): number => {
  if (stepsTotal === 0) return 0;
  return Math.min(stepsCompleted / stepsTotal, 1);
};

const renderLevelIcon = (item: DashboardAction) => {
  const { status, steps_completed, steps_total } = item.attributes;

  // Show circular progress for in_progress items only
  if (status.startsWith(LEVEL_STATUS.IN_PROGRESS) && steps_total > 0) {
    const progress = calculateProgress(steps_completed, steps_total);
    return <CircleProgress value={progress} className={styles.levelIcon} progressColor="#D7E251" />;
  }

  // Use static SVG icons for all other statuses
  return <SVG className={styles.levelIcon} src={getLevelIcon(status)} />;
};

const Workflow = ({
  items,
  confirmAction,
  isCurrentUserAssignedToInquiry,
  addMessage
}: NewWorkflowProps) => {
  if (!items) return;
  return (
    <ul className={styles.wrapper}>
      {items.map(item => {
        const greyedOutStyles =
          item.attributes.status.startsWith(LEVEL_STATUS.ACTIVE) ||
          item.attributes.status.startsWith(LEVEL_STATUS.IN_PROGRESS) ||
          item.attributes.status === LEVEL_STATUS.COMPLETED
            ? ""
            : styles.greyedOutStep;

        return (
          <li className={styles.levelWrapper} key={item.id}>
            <div className={styles.levelItem}>
              {renderLevelIcon(item)}
              <span className={cx(styles.levelName, greyedOutStyles)}>
                {item.statusLabels && item.attributes.status !== LEVEL_STATUS.INACTIVE
                  ? item.statusLabels[item.attributes.status].heading
                  : item.heading}
              </span>
              {item.attributes.is_required === false && (
                <span className={cx(styles.requiredBadge, greyedOutStyles)}>Optional</span>
              )}
            </div>
            <div className={cx(styles.stepsWrapper, greyedOutStyles)}>
              {item.statusLabels && item.attributes.status !== LEVEL_STATUS.INACTIVE
                ? item.statusLabels[item.attributes.status].label
                : item.label}
              {item.actions && item.actions.length > 0 && (
                <div className={styles.workFlowActionButtonContainer}>
                  {item.assignee && (
                    <div className={styles.assignee}>
                      {`Bearbeiter: ${item.assignee.firstName} ${item.assignee.lastName}`}
                    </div>
                  )}
                  {item.actions.map((action, index) => (
                    <WorkflowActionButton
                      key={`${item.id}_action_${index}`}
                      customStyles={styles.workFlowActionButton}
                      isDisabled={
                        item.attributes.status === LEVEL_STATUS.INACTIVE ||
                        item.attributes.status.startsWith(LEVEL_STATUS.IN_PROGRESS) ||
                        action.isDisabled ||
                        item.attributes.disabled
                      }
                      actionId={item.id}
                      title={action.buttonLabel}
                      confirmAction={confirmAction}
                      completedOrFailed={
                        item.attributes.status.startsWith(LEVEL_STATUS.FAILED) ||
                        item.attributes.status === LEVEL_STATUS.COMPLETED
                      }
                      description={item.label}
                      customModal={action.customModal || null}
                      addMessage={addMessage}
                      isRepetable={action.actionIsRepetable}
                      isCurrentUserAssignedToInquiry={isCurrentUserAssignedToInquiry}
                      onClick={action.onClick}
                    />
                  ))}
                </div>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
};

export default Workflow;
